import { getRawRoute } from '@/utils/index'
import { PageEnum } from '@/enums/pageEnum'
import { PAGE_NOT_FOUND_ROUTE } from '@/router/routes/basic'

const getToTarget = (tabItem) => {
  const { params, path, query } = tabItem
  return {
    params: params || {},
    path,
    query: query || {},
  }
}

const getSessionTabList = () => {
  const tabs = sessionStorage.getItem('tabs')
  if (!tabs) return []
  const list = JSON.parse(tabs)
  if (!list || !list.length) return []
  sessionStorage.removeItem('tabs')
  return list
}

const state = {
  tabList: getSessionTabList(),
  cachedTabList: new Set(),
}

const getters = {
  getTabList: (state) => {
    return state.tabList
  },

  getCachedTabList: (state) => {
    return Array.from(state.cachedTabList)
  },
}

const mutations = {
  ADD_TAB(state, tab) {
    if (state.tabList.some((t) => t.path === tab.path)) return

    state.tabList.push({ ...tab, title: tab.meta.title || 'no-name' })
  },

  DELETE_TAB(state, tab) {
    for (const [i, t] of state.tabList.entries()) {
      if (t.path === tab.path) {
        state.tabList.splice(i, 1)
        break
      }
    }
  },

  DELETE_OTHER_TABS(state, tab) {
    state.tabList = state.tabList.filter((t) => {
      return t.meta.affix || t.path === tab.path
    })
  },

  DELETE_ALL_TABS(state) {
    // keep affix tab
    state.tabList = state.tabList.filter((t) => t.meta.affix)
  },

  UPDATE_CACHED_TAB_LIST(state) {
    const cacheMap = new Set()

    for (const tab of state.tabList) {
      const item = getRawRoute(tab)
      // Ignore the cache
      const needCache = item.meta && !item.meta.ignoreKeepAlive
      if (needCache) {
        cacheMap.add(item.name)
      }
    }
    state.cachedTabList = cacheMap
  },

  CLEAR_CACHE(state) {
    state.cachedTabList = new Set()
  },

  SAVE_TABLIST_IN_SESSION(state) {
    const { tabList } = state
    const list = tabList.map((item) => {
      const { name, hash, fullPath, path, query, params, meta } = item
      return {
        name,
        hash,
        fullPath,
        path,
        query,
        params,
        meta,
      }
    })
    sessionStorage.setItem('tabs', JSON.stringify(list))
  },

  RESET_STATE(state) {
    state.tabList = []
    state.cachedTabList = new Set()
  },
}

const actions = {
  addTab({ state, getters, commit }, tab) {
    const { path, name, fullPath, params, query } = getRawRoute(tab)

    if (PageEnum.BASE_LOGIN.includes(path) || !name || [PAGE_NOT_FOUND_ROUTE.name].includes(name)) {
      return
    }

    let updateIndex = -1

    const tabHasExist = state.tabList.some((tabItem, index) => {
      updateIndex = index
      return (tabItem.fullPath || tabItem.path) === (fullPath || path)
    })

    if (tabHasExist) {
      const tabList = getters.getTabList
      const curTab = tabList[updateIndex]

      if (!curTab) {
        return
      }

      curTab.params = params || curTab.params
      curTab.query = query || curTab.query
      curTab.fullPath = fullPath || curTab.fullPath

      state.tabList.splice(updateIndex, 1, curTab)
    } else {
      commit('ADD_TAB', tab)
    }

    commit('UPDATE_CACHED_TAB_LIST')
  },

  async closeTab({ state, commit, getters }, tab) {
    return new Promise((resolve) => {
      commit('DELETE_TAB', tab)
      commit('UPDATE_CACHED_TAB_LIST')

      resolve({
        tabList: [...state.tabList],
        cachedTabList: getters.getCachedTabList,
      })
    })
  },

  async closeTabByKey({ state, dispatch }, { key, router }) {
    const index = state.tabList.findIndex((tab) => (tab.fullPath || tab.path) === key)

    if (index !== -1) {
      await dispatch('closeTab', state.tabList[index])

      const { currentRoute } = router
      const { tabList } = state
      // 检查当前路由是否存在于tabList中
      const isActivated = tabList.findIndex((tab) => tab.fullPath === currentRoute.fullPath)

      // 如果当前路由不存在于TabList中，尝试切换到其它路由
      if (isActivated === -1) {
        let pageIndex = index
        if (index === tabList.length) {
          pageIndex = index - 1
        }

        if (pageIndex >= 0) {
          const page = tabList[pageIndex]
          const toTarget = getToTarget(page)
          return toTarget
        }
      }
    }

    return null
  },

  async closeOtherTabs({ state, commit, getters }, tab) {
    return new Promise((resolve) => {
      commit('DELETE_OTHER_TABS', tab)
      commit('UPDATE_CACHED_TAB_LIST')

      resolve({
        tabList: [...state.tabList],
        cachedTabList: getters.getCachedTabList,
      })
    })
  },

  async closeAllTabs({ state, commit, getters }) {
    return new Promise((resolve) => {
      commit('DELETE_ALL_TABS')
      commit('UPDATE_CACHED_TAB_LIST')

      resolve({
        tabList: [...state.tabList],
        cachedTabList: getters.getCachedTabList,
      })
    })
  },

  async deleteCachedTab({ state }, name) {
    return new Promise((resolve) => {
      if (state.cachedTabList.has(name)) {
        state.cachedTabList.delete(name)
      }
      const cacheMap = new Set()
      for (const item of state.cachedTabList.values()) {
        cacheMap.add(item)
      }

      state.cachedTabList = cacheMap
      resolve()
    })
  },
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
}
